import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom';
import {
  AppProvider,
  Frame,
  Navigation,
  TopBar,
  Page,
  Button,
  Icon,
  InlineStack,
  Toast,
} from '@shopify/polaris';
import {
  HomeMajor,
  SettingsMajor,
  ContentMinor,
  AddMajor,
  ImageMajor,
  LogOutMinor,
  ProfileMinor,
  PlayMinor,
  CameraMajor,
  ViewMajor,
  RefreshMinor,
  RefreshMajor,
  GaugeMinor,
  MagicMinor,
  PaintBrushMajor,
  MaximizeMinor,
  IconsMajor,
  StarFilledMinor,
  ExploreImagesMajor,
  ToolsMajor,
  ClockMajor,
  EditMinor,
  FlagMajor,
  ChatMajor,
  CustomerPlusMajor,
  ExportMinor,
  ArrowLeftMinor,
  OrdersMajor,
  PrintMinor,
  FinancesMinor,
  GlobeMajor
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import './modal-overrides.css';
import './dashboard.css';

// Import logo
import logoWhite from './assets/LogoWhite.png';

// Import services
import { authService } from './services/authService';
import { getServerURL } from './services/config';
import { isReviewerPathAllowed } from './utils/orderPrintingAccess';
import { isOrderDetailPath } from './utils/orderRoutes';

// Import components
import AuthProvider, { useAuth } from './components/AuthProvider';

// Import routes
import AppRoutes from './routes/AppRoutes';

// Import custom hook
import useProfileMenuWidthSync from './useProfileMenuWidthSync';

// Define SERVER_URL constant
const SERVER_URL = getServerURL();

if (!SERVER_URL) {
  console.error('REACT_APP_SERVER_URL environment variable is not set');
}

// Add this style block at the top level (e.g., after imports or at the end of the file)
// This will center the icon vertically in the button for the modal secondary actions
const buttonIconCenterStyle = document.createElement('style');
buttonIconCenterStyle.innerHTML = `
  .Polaris-Modal-Dialog__Modal .Polaris-Button__Icon {
    display: flex;
    align-items: center;
    height: 100%;
  }
`;
document.head.appendChild(buttonIconCenterStyle);

// Inner component that has access to routing hooks
function AppContent() {
  useProfileMenuWidthSync();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, handleLogout } = useAuth();

  const [theme, setTheme] = useState('light');
  const [userMenuActive, setUserMenuActive] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const logoInsertedRef = useRef(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('Settings Saved');
  const [activeCountry, setActiveCountry] = useState(authService.getActiveCountry());

  // Add state for system status
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  // System status for navbar credits and dashboard refresh
  const [systemStatus, setSystemStatus] = useState(null);

  const fetchSystemStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    try {
      if (!authService.isAuthenticated()) return;
      const currentUser = authService.getUser();
      if (!currentUser?.permissions?.includes('view_system_status')) return;
      const response = await fetch(`${SERVER_URL}/system-status`, {
        headers: authService.getAuthHeaders(),
      });
      if (response.ok) {
        const result = await response.json();
        setSystemStatus(result);

        // Dispatch a custom event to notify other components (like DashboardPage) to refresh if needed
        // although DashboardPage fetches its own data, this button is in the TopBar essentially (via Page title action)
        window.dispatchEvent(new Event('refreshDashboardData'));
      }
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      setToastMessage('Failed to fetch system status');
      setToastActive(true);
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const nextCountry = authService.getActiveCountry();
    setActiveCountry(nextCountry);
  }, [user]);

  const availableCountries = authService.getAllowedCountries(user);

  const handleCountrySwitch = useCallback((country) => {
    if (country === activeCountry) return;
    authService.setActiveCountry(country);
    const resolvedCountry = authService.getActiveCountry();
    setActiveCountry(resolvedCountry);
    window.dispatchEvent(new CustomEvent('countryChanged', { detail: { country: resolvedCountry } }));
    window.location.reload();
  }, [activeCountry]);

  useEffect(() => {
    if (user) {
      fetchSystemStatus();
      // Refresh every 5 minutes
      const interval = setInterval(fetchSystemStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, fetchSystemStatus]);

  const [isCreateSceneModalOpen, setIsCreateSceneModalOpen] = useState(false);

  // Permission helpers
  const isSuperAdmin = (user?.group || '').toLowerCase() === 'super admin';
  const hasManageDesign = Array.isArray(user?.permissions) && user.permissions.includes('manage_design');
  const hasManagePreviews = Array.isArray(user?.permissions) && user.permissions.includes('manage_previews');
  const hasManagePrintings = Array.isArray(user?.permissions) && user.permissions.includes('manage_printings');
  const hasManageStories = Array.isArray(user?.permissions) && user.permissions.includes('manage_stories');
  const hasViewOrders = Array.isArray(user?.permissions) && user.permissions.includes('view_orders');
  const hasManageOrders = Array.isArray(user?.permissions) && user.permissions.includes('manage_orders');
  const isDesignerOnly = hasManageDesign && !isSuperAdmin && Array.isArray(user?.permissions) && user.permissions.every((perm) => perm === 'manage_design');
  const isPreviewerOnly = hasManagePreviews && !isSuperAdmin && !hasManageDesign && ((user?.group || '').toLowerCase() === 'previewer' || (Array.isArray(user?.permissions) && user.permissions.every((perm) => perm === 'manage_previews')));
  const printingRolePermissions = new Set([
    'manage_printings',
    'view_orders',
    'manage_orders',
    'review_orders',
    'manage_previews',
  ]);
  const isPrintingsOnly = hasManagePrintings
    && !isSuperAdmin
    && !hasManageDesign
    && (
      (user?.group || '').toLowerCase() === 'printings'
      || (
        Array.isArray(user?.permissions)
        && user.permissions.every((permission) => printingRolePermissions.has(permission))
      )
    );
  const isReviewerOnly = hasViewOrders && !isSuperAdmin && !hasManageDesign && !hasManagePreviews && ((user?.group || '').toLowerCase() === 'reviewer' || (Array.isArray(user?.permissions) && user.permissions.every((perm) => perm === 'view_orders')));
  const isDataEntry = Array.isArray(user?.permissions) && user.permissions.includes('data_entry');
  const hasViewOfflineOrders = Array.isArray(user?.permissions) && user.permissions.includes('view_offline_orders');
  const hasManageOfflineOrders = Array.isArray(user?.permissions) && user.permissions.includes('manage_offline_orders');

  // Customer Service role helpers
  const isCustomerService = (user?.group || '').toLowerCase() === 'customer service' ||
    (Array.isArray(user?.permissions) &&
      user.permissions.includes('view_orders') &&
      user.permissions.includes('manage_trials') &&
      user.permissions.includes('manage_tickets') &&
      !isSuperAdmin);
  const isCustomerServiceOnly = isCustomerService && !isSuperAdmin && !hasManageDesign && !hasManagePreviews;

  // Operations role helpers
  const isOperations = (user?.group || '').toLowerCase() === 'operations' ||
    (Array.isArray(user?.permissions) &&
      user.permissions.includes('manage_orders') &&
      user.permissions.includes('manage_tickets') &&
      user.permissions.includes('manage_trials') &&
      user.permissions.includes('manage_users') &&
      !isSuperAdmin);

  const isOperationsOnly = isOperations && !isSuperAdmin && !hasManageDesign && !hasManagePreviews;

  // Offline Orders role helpers
  const isOfflineOrdersOnly = ((user?.group || '').toLowerCase() === 'offline orders' ||
    ((hasViewOfflineOrders || hasManageOfflineOrders) &&
      !isCustomerService && !isOperations)) &&
    !isSuperAdmin && !hasManageDesign;

  // Toast helper function
  const showToast = (message) => {
    setToastMessage(message);
    setToastActive(true);
    setTimeout(() => {
      setToastActive(false);
    }, 3000);
  };

  // Show login success toast when component first loads (user just logged in)
  useEffect(() => {
    // Only show this once when the component mounts (after successful login)
    const hasShownLoginToast = sessionStorage.getItem('loginToastShown');
    if (!hasShownLoginToast) {
      showToast('Successfully logged in!');
      sessionStorage.setItem('loginToastShown', 'true');
    }

    // Clean up on logout (when component unmounts)
    return () => {
      sessionStorage.removeItem('loginToastShown');
    };
  }, []);

  // Handle logout with toast
  const handleLogoutWithToast = () => {
    handleLogout(showToast);
  };

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme]);


  useEffect(() => {
    if (logoInsertedRef.current) return; // Prevent multiple insertions

    const insertLogo = () => {
      const leftContent = document.querySelector('.Polaris-TopBar__LeftContent');
      if (leftContent && !leftContent.querySelector('img[alt="Stories By Snaps Logo"]')) {
        const logoElement = document.createElement('img');
        logoElement.src = logoWhite;
        logoElement.alt = 'Stories By Snaps Logo';
        logoElement.style.width = 'auto';
        logoElement.style.height = '40px';
        logoElement.style.verticalAlign = 'middle';
        logoElement.style.marginRight = '12px';
        logoElement.style.paddingLeft = 'var(--p-space-300)';

        leftContent.insertBefore(logoElement, leftContent.firstChild);
        logoInsertedRef.current = true;
      }

      // Set TopBar height to 56px
      const topBar = document.querySelector('.Polaris-TopBar');
      if (topBar) {
        topBar.style.height = '56px';
        topBar.style.minHeight = '56px';
      }
    };

    // Try immediately
    insertLogo();

    // If not found, try again after a short delay
    if (!document.querySelector('.Polaris-TopBar__LeftContent')) {
      setTimeout(insertLogo, 100);
    }
  }, []);

  // Inject Credits Box into TopBar
  useEffect(() => {
    const updateCreditsBox = () => {
      const rightContent = document.querySelector('.Polaris-TopBar__RightContent');
      if (!rightContent) return;

      let creditsBox = document.getElementById('system-credits-box');

      if (!creditsBox) {
        creditsBox = document.createElement('div');
        creditsBox.id = 'system-credits-box';
        // Light blue background, styling
        // Light blue background, styling
        creditsBox.style.backgroundColor = '#f0f9ff'; // Very light blue (Tailwind sky-50)
        creditsBox.style.color = '#0c4a6e'; // Darker blue text
        creditsBox.style.padding = '0 12px';
        creditsBox.style.height = '32px';
        creditsBox.style.borderRadius = '4px';
        creditsBox.style.margin = '12px';
        creditsBox.style.display = 'flex';
        creditsBox.style.alignItems = 'center';
        creditsBox.style.gap = '8px';
        creditsBox.style.fontSize = '12px';
        creditsBox.style.fontWeight = '500';
        creditsBox.style.whiteSpace = 'nowrap';
        // creditsBox.style.className = 'Polaris-TopBar-Menu__ActivatorWrapper'; 



        // Insert as first child of RightContent
        rightContent.insertBefore(creditsBox, rightContent.firstChild);
      }

      if (isLoadingStatus || !systemStatus) {
        // Ensure keyframes exist
        if (!document.getElementById('credits-spinner-style')) {
          const style = document.createElement('style');
          style.id = 'credits-spinner-style';
          style.innerHTML = `
                @keyframes spin-credits {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `;
          document.head.appendChild(style);
        }

        // Spinner SVG
        creditsBox.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px;">
            <svg viewBox="0 0 20 20" class="Polaris-Spinner Polaris-Spinner--sizeSmall" style="width: 20px; height: 20px; fill: currentColor; animation: spin-credits 1s linear infinite;">
              <path d="M7.229 1.173a9.25 9.25 0 1011.655 11.412 1.25 1.25 0 10-2.4-.698 6.75 6.75 0 11-8.506-8.329 1.25 1.25 0 10-.75-2.385z"></path>
            </svg>
            <span>Loading Credits...</span>
          </div>
        `;
      } else {
        const reGenerationsVal = systemStatus?.credits?.bfl?.remainingReGenerations;
        const storiesVal = systemStatus?.credits?.comfy?.remainingStories;

        const reGenerations = reGenerationsVal != null ? Math.round(reGenerationsVal) : '-';
        const stories = storiesVal != null ? Math.round(storiesVal) : '-';

        // Check for low credits
        const isLowCredits = (reGenerationsVal != null && reGenerationsVal < 100) ||
          (storiesVal != null && storiesVal < 25);

        if (isLowCredits) {
          creditsBox.style.backgroundColor = '#fee2e2'; // Red-100
          creditsBox.style.color = '#991b1b'; // Red-800
        } else {
          creditsBox.style.backgroundColor = '#f0f9ff'; // Sky-50
          creditsBox.style.color = '#0c4a6e'; // Sky-900
        }

        creditsBox.innerHTML = `
          <span>Remaining ReGenerations : ${reGenerations}</span>
          <span style="width: 1px; height: 16px; background-color: ${isLowCredits ? '#ef4444' : '#7dd3fc'};"></span>
          <span>Remaining Stories : ${stories}</span>
        `;
      }
    };

    // Try to update immediately and after a delay to ensure TopBar is rendered
    updateCreditsBox();
    const timer = setTimeout(updateCreditsBox, 500);
    return () => clearTimeout(timer);
  }, [systemStatus, isLoadingStatus]);

  // Route access control based on permissions
  useEffect(() => {
    const path = location.pathname;
    if (path === '/orders/new' && !(isSuperAdmin || hasManageOrders)) {
      navigate('/orders', { replace: true });
      return;
    }
    if (isDesignerOnly) {
      const allowed = path.startsWith('/ai-design') || path === '/profile';
      if (!allowed) {
        navigate('/ai-design/imagine', { replace: true });
      }
      return;
    }
    if (isPrintingsOnly) {
      const allowedPrintings = path.startsWith('/printings')
        || path === '/orders'
        || path.startsWith('/orders/')
        || path === '/generation/content'
        || path === '/profile'
        || path === '/';
      if (!allowedPrintings) {
        navigate('/printings', { replace: true });
      }
      return;
    }
    if (isPreviewerOnly) {
      const allowedPreviewer = path === '/previewer/content'
        || path === '/generation/content'
        || path === '/profile'
        || path === '/'
        || (hasManagePrintings && path.startsWith('/printings'));
      if (!allowedPreviewer) {
        navigate('/previewer/content', { replace: true });
      }
      return;
    }
    if (isDesignerOnly) {
      const allowedReviewer = path === '/generation/content' || path === '/profile' || path === '/';
      if (!allowedReviewer) {
        navigate('/generation/content', { replace: true });
      }
      return;
    }
    if (isReviewerOnly) {
      if (!isReviewerPathAllowed(path)) {
        navigate('/orders', { replace: true });
      }
      return;
    }
    if (isCustomerServiceOnly) {
      const allowedCustomerService = path === '/orders' ||
        path.startsWith('/orders/') ||
        path === '/trials' ||
        path.startsWith('/trials/') ||
        path === '/tickets' ||
        path.startsWith('/tickets/') ||
        path === '/profile' ||
        path === '/';
      if (!allowedCustomerService) {
        navigate('/orders', { replace: true });
      }
      return;
    }
    if (isOperationsOnly) {
      const allowedOperations = path === '/orders' ||
        path.startsWith('/orders/') ||
        path === '/trials' ||
        path.startsWith('/trials/') ||
        path === '/tickets' ||
        path.startsWith('/tickets/') ||
        path === '/users' ||
        path.startsWith('/accounting/') ||
        path === '/settlements' ||
        path === '/commissions' ||
        path === '/budgets' ||
        path === '/taxes' ||
        path === '/financial-reports' ||
        path === '/profile' ||
        path === '/' ||
        (hasManagePrintings && path.startsWith('/printings'));
      if (!allowedOperations) {
        navigate('/orders', { replace: true });
      }
      return;
    }
    if (isDataEntry) {
      const allowedDataEntry = path === '/data-entry' || path === '/profile' || path === '/';
      if (!allowedDataEntry) {
        navigate('/data-entry', { replace: true });
      }
      return;
    }
    if (isOfflineOrdersOnly) {
      const allowedOffline = path === '/offline/previews' ||
        path === '/offline/orders' ||
        path === '/offline/generations' ||
        path.startsWith('/orders/') ||
        path === '/generation/content' ||
        path === '/profile' ||
        path === '/';
      if (!allowedOffline) {
        navigate('/offline/previews', { replace: true });
      }
      return;
    }
    if (path.startsWith('/accounting/') && !isSuperAdmin && !isOperations) {
      navigate('/', { replace: true });
      return;
    }
    // Block AI Design routes if not permitted
    if (!isSuperAdmin && !hasManageDesign && path.startsWith('/ai-design')) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, isDesignerOnly, isReviewerOnly, isPrintingsOnly, isPreviewerOnly, isCustomerServiceOnly, isSuperAdmin, isOperations, hasManageDesign, hasManagePrintings, navigate, isDataEntry, isOperationsOnly, isOfflineOrdersOnly, hasManageOrders]);

  const userMenuActions = [
    ...(availableCountries.length > 1
      ? [{
        items: availableCountries.map((country) => ({
          content: country === activeCountry ? `Country: ${country} (Current)` : `Switch to ${country}`,
          onAction: () => handleCountrySwitch(country),
        })),
      }]
      : []),
    {
      items: [
        {
          content: 'Profile',
          icon: ProfileMinor,
          onAction: () => {
            navigate('/profile');
          },
        },
      ],
    },
    {
      items: [
        {
          content: 'Sign out',
          icon: LogOutMinor,
          onAction: handleLogoutWithToast,
        },
      ],
    },
  ];

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={
        <TopBar.UserMenu
          actions={userMenuActions}
          name={user?.name || user?.email || "Admin"}
          detail={`${user?.email || "Stories By Snaps"} (${activeCountry})`}
          open={userMenuActive}
          onToggle={setUserMenuActive}
        />
      }
      secondaryActions={[
        {
          icon: theme === 'light' ? 'MoonIcon' : 'SunIcon',
          accessibilityLabel: 'Toggle theme',
          onClick: toggleTheme,
        },
      ]}
      onNavigationToggle={() => setMobileNavigationActive(true)}
    />
  );

  const accountingNavigationItems = [
    {
      label: 'Summary',
      icon: FinancesMinor,
      onClick: () => navigate('/accounting/summary'),
      selected: location.pathname === '/accounting/summary',
    },
    {
      label: 'InstaPay Payments',
      icon: FinancesMinor,
      onClick: () => navigate('/accounting/payments'),
      selected: location.pathname === '/accounting/payments',
    },
    {
      label: 'Payouts',
      icon: FinancesMinor,
      onClick: () => navigate('/accounting/payouts'),
      selected: location.pathname === '/accounting/payouts',
    },
  ];

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      {isPrintingsOnly ? (
        <>
          <Navigation.Section
            title="Operations"
            items={[
              {
                label: 'Orders',
                icon: OrdersMajor,
                onClick: () => navigate('/orders'),
                selected: location.pathname === '/orders'
                  || location.pathname.startsWith('/orders/'),
              },
              {
                label: 'Generations',
                icon: CameraMajor,
                onClick: () => navigate('/generation/content'),
                selected: location.pathname === '/generation/content',
              },
            ]}
          />
          <Navigation.Section
            title="Printings"
            items={[
              {
                label: 'Printings',
                icon: ExportMinor,
                onClick: () => navigate('/printings'),
                selected: location.pathname === '/printings',
              },
              {
                label: 'Express Printings',
                icon: ExportMinor,
                onClick: () => navigate('/printings/express'),
                selected: location.pathname === '/printings/express',
              }
            ]}
          />
          <Navigation.Section
            title="Settings"
            items={[
              {
                label: 'Profile',
                icon: ProfileMinor,
                onClick: () => navigate('/profile'),
                selected: location.pathname === '/profile',
              },
            ]}
          />
        </>
      ) : isOperationsOnly ? (
        <>
          <Navigation.Section
            title="Operations"
            items={[
              {
                label: 'Orders',
                icon: OrdersMajor,
                onClick: () => navigate('/orders'),
                selected: location.pathname === '/orders' || location.pathname.startsWith('/orders/'),
              },
              // {
              //   label: 'Trials',
              //   icon: OrdersMajor,
              //   onClick: () => navigate('/trials'),
              //   selected: location.pathname === '/trials',
              // },
              // {
              //   label: 'Tickets',
              //   icon: FlagMajor,
              //   onClick: () => navigate('/tickets'),
              //   selected: location.pathname === '/tickets' || location.pathname.startsWith('/tickets/'),
              // },
              {
                label: 'Users',
                icon: CustomerPlusMajor,
                onClick: () => navigate('/users'),
                selected: location.pathname === '/users',
              },
            ]}
          />
          {hasManagePrintings && (
            <Navigation.Section
              title="Printings"
              items={[
                {
                  label: 'Printings',
                  icon: ExportMinor,
                  onClick: () => navigate('/printings'),
                  selected: location.pathname === '/printings',
                },
                {
                  label: 'Express Printings',
                  icon: ExportMinor,
                  onClick: () => navigate('/printings/express'),
                  selected: location.pathname === '/printings/express',
                },
              ]}
            />
          )}
          <Navigation.Section
            title="Accounting"
            items={accountingNavigationItems}
          />
          <Navigation.Section
            title="Settings"
            items={[
              {
                label: 'Profile',
                icon: ProfileMinor,
                onClick: () => navigate('/profile'),
                selected: location.pathname === '/profile',
              },
            ]}
          />
        </>
      ) : isOfflineOrdersOnly ? (
        <>
          <Navigation.Section
            title="Offline Orders"
            items={[
              {
                label: 'Previews',
                icon: CameraMajor,
                onClick: () => navigate('/offline/previews'),
                selected: location.pathname === '/offline/previews',
              },
              {
                label: 'Orders',
                icon: OrdersMajor,
                onClick: () => navigate('/offline/orders'),
                selected: location.pathname === '/offline/orders',
              },
              {
                label: 'Generations',
                icon: CameraMajor,
                onClick: () => navigate('/offline/generations'),
                selected: location.pathname === '/offline/generations',
              },
            ]}
          />
          <Navigation.Section
            title="Settings"
            items={[
              {
                label: 'Profile',
                icon: ProfileMinor,
                onClick: () => navigate('/profile'),
                selected: location.pathname === '/profile',
              },
            ]}
          />
        </>
      ) : isCustomerServiceOnly ? (
        <>
          <Navigation.Section
            title="Customer Service"
            items={[
              {
                label: 'Orders',
                icon: OrdersMajor,
                onClick: () => navigate('/orders'),
                selected: location.pathname === '/orders' || location.pathname.startsWith('/orders/'),
              },
              // {
              //   label: 'Trials',
              //   icon: OrdersMajor,
              //   onClick: () => navigate('/trials'),
              //   selected: location.pathname === '/trials',
              // },
              // {
              //   label: 'Tickets',
              //   icon: FlagMajor,
              //   onClick: () => navigate('/tickets'),
              //   selected: location.pathname === '/tickets' || location.pathname.startsWith('/tickets/'),
              // },
            ]}
          />
          <Navigation.Section
            title="Settings"
            items={[
              {
                label: 'Profile',
                icon: ProfileMinor,
                onClick: () => navigate('/profile'),
                selected: location.pathname === '/profile',
              },
            ]}
          />
        </>
      ) : isPreviewerOnly ? (
        <>
          <Navigation.Section
            title="Previewer"
            items={[
              {
                label: 'Previews',
                icon: CameraMajor,
                onClick: () => navigate('/previewer/content'),
                selected: location.pathname === '/previewer/content',
              },
            ]}
            action={{ icon: ViewMajor }}
          />
          <Navigation.Section
            title="Generation"
            items={[
              {
                label: 'Generations',
                icon: CameraMajor,
                onClick: () => navigate('/generation/content'),
                selected: location.pathname === '/generation/content',
              },
              ...(hasManagePrintings ? [
                {
                  label: 'Printings',
                  icon: ExportMinor,
                  onClick: () => navigate('/printings'),
                  selected: location.pathname === '/printings',
                },
                {
                  label: 'Express Printings',
                  icon: ExportMinor,
                  onClick: () => navigate('/printings/express'),
                  selected: location.pathname === '/printings/express',
                },
              ] : []),
            ]}
            action={{ icon: PlayMinor }}
          />
          <Navigation.Section
            title="Settings"
            items={[
              {
                label: 'Profile',
                icon: ProfileMinor,
                onClick: () => navigate('/profile'),
                selected: location.pathname === '/profile',
              },
            ]}
          />
        </>
      ) : (
        <>
          {!isDesignerOnly && (
            <Navigation.Section
              items={[
                {
                  label: 'Dashboard',
                  icon: HomeMajor,
                  onClick: () => navigate('/dashboard'),
                  selected: location.pathname === '/dashboard',
                }
              ]}
            />
          )}



          {/* {isSuperAdmin && (
            <Navigation.Section
              title="Previewer"
              items={[
                {
                  label: 'Stories',
                  icon: ContentMinor,
                  onClick: () => navigate('/previewer/stories'),
                  selected: location.pathname === '/previewer/stories',
                },
                {
                  label: 'Scenes',
                  icon: ImageMajor,
                  onClick: () => navigate('/previewer/scenes'),
                  selected: location.pathname === '/previewer/scenes',
                },
                {
                  label: 'Previews',
                  icon: CameraMajor,
                  onClick: () => navigate('/previewer/content'),
                  selected: location.pathname === '/previewer/content',
                },
              ]}
              action={{
                icon: ViewMajor,
              }}
            />
          )} */}

          {(isSuperAdmin || hasManageStories) && (
            <Navigation.Section
              title="Generation"
              items={[
                ...((isSuperAdmin || hasManageStories) ? [{
                  label: 'Stories',
                  icon: ContentMinor,
                  onClick: () => navigate('/generation/stories'),
                  selected: location.pathname === '/generation/stories',
                }, {
                  label: 'Montage Profiles',
                  icon: SettingsMajor,
                  onClick: () => navigate('/generation/montage-profiles'),
                  selected: location.pathname === '/generation/montage-profiles',
                }] : []),
                ...((isSuperAdmin || hasManageStories) ? [{
                  label: 'Scenes',
                  icon: ImageMajor,
                  onClick: () => navigate('/generation/scenes'),
                  selected: location.pathname === '/generation/scenes',
                }] : []),
              ]}
              action={{
                icon: PlayMinor,
              }}
            />
          )}

          {isSuperAdmin && (
            <Navigation.Section
              title="Orders"
              items={[
                // {
                //   label: 'Trials',
                //   icon: OrdersMajor,
                //   onClick: () => navigate('/trials'),
                //   selected: location.pathname === '/trials',
                // },
                {
                  label: 'Orders',
                  icon: OrdersMajor,
                  onClick: () => navigate('/orders'),
                  selected: location.pathname === '/orders' || location.pathname.startsWith('/orders/'),
                },
                {
                  label: 'Generations',
                  icon: CameraMajor,
                  onClick: () => navigate('/generation/content'),
                  selected: location.pathname === '/generation/content',
                },
                {
                  label: 'Printings',
                  icon: ExportMinor,
                  onClick: () => navigate('/printings'),
                  selected: location.pathname === '/printings',
                },
                {
                  label: 'Express Printings',
                  icon: ExportMinor,
                  onClick: () => navigate('/printings/express'),
                  selected: location.pathname === '/printings/express',
                },
                // {
                //   label: 'Tickets',
                //   icon: FlagMajor,
                //   onClick: () => navigate('/tickets'),
                //   selected: location.pathname === '/tickets' || location.pathname.startsWith('/tickets/'),
                // },
              ]}
              action={{
                icon: PlayMinor,
              }}
            />
          )}

          {(isReviewerOnly || hasManageDesign) && (
            <Navigation.Section
              title="Orders"
              items={[
                ...(isReviewerOnly ? [{
                  label: 'Orders',
                  icon: OrdersMajor,
                  onClick: () => navigate('/orders'),
                  selected: location.pathname === '/orders'
                    || location.pathname.startsWith('/orders/'),
                }] : []),
                ...(hasManageDesign ? [{
                  label: 'Generations',
                  icon: CameraMajor,
                  onClick: () => navigate('/generation/content'),
                  selected: location.pathname === '/generation/content',
                }] : []),
              ]}
              action={{
                icon: PlayMinor,
              }}
            />
          )}

          {(isSuperAdmin || isOperations) && (
            <Navigation.Section
              title="Accounting"
              items={accountingNavigationItems}
              action={{
                icon: FinancesMinor,
              }}
            />
          )}

          <Navigation.Section
            title="Settings"
            items={[
              {
                label: 'Profile',
                icon: ProfileMinor,
                onClick: () => navigate('/profile'),
                selected: location.pathname === '/profile',
              },
              ...(!isDesignerOnly
                ? [
                  {
                    label: 'Settings',
                    icon: SettingsMajor,
                    onClick: () => navigate('/settings'),
                    selected: location.pathname === '/settings',
                  },
                  {
                    label: 'AI Models',
                    icon: SettingsMajor,
                    onClick: () => navigate('/settings/ai-models'),
                    selected: location.pathname === '/settings/ai-models',
                  },
                  ...(isSuperAdmin
                    ? [
                      {
                        label: 'Users',
                        icon: CustomerPlusMajor,
                        onClick: () => navigate('/users'),
                        selected: location.pathname === '/users',
                      },
                      {
                        label: 'Admin Settings',
                        icon: SettingsMajor,
                        onClick: () => navigate('/admin-settings'),
                        selected: location.pathname === '/admin-settings',
                      },
                    ]
                    : []),
                ]
                : []),
            ]}
          />
        </>
      )}
    </Navigation>
  );



  // Helper function to get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (/^\/printings\/[^/]+\/verify$/.test(path)) {
      return (
        <InlineStack align="center" gap="100">
          <Icon source={ExportMinor} color="base" />
          <span>Printings Verify</span>
        </InlineStack>
      );
    }
    if (path === '/generation/montage-profiles') {
      return (
        <InlineStack align="center" gap="100">
          <Icon source={SettingsMajor} color="base" />
          <span>Montage Profiles</span>
        </InlineStack>
      );
    }
    switch (path) {
      case '/':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <Icon source={GaugeMinor} tone="base" />
            </span>
            <span style={{ fontWeight: 700, fontSize: 24, color: '#3b3b3b' }}>Dashboard</span>
            <span style={{ marginLeft: 'auto' }}>
              <Button
                icon={RefreshMajor}
                onClick={fetchSystemStatus}
                loading={isLoadingStatus}
                accessibilityLabel="Refresh system status"
                variant="tertiary"
              >
                Refresh
              </Button>
            </span>
          </div>
        );
      case '/previewer/stories':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ContentMinor} color="base" />
            <span>Previewer Stories</span>
          </InlineStack>
        );
      case '/previewer/scenes':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ImageMajor} color="base" />
            <span>Previewer Scenes</span>
          </InlineStack>
        );
      case '/generation/stories':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ContentMinor} color="base" />
            <span>Generation Stories</span>
          </InlineStack>
        );
      case '/generation/scenes':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ImageMajor} color="base" />
            <span>Generation Scenes</span>
          </InlineStack>
        );
      case '/previewer/content':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={CameraMajor} color="base" />
            <span>Previews</span>
          </InlineStack>
        );
      case '/generation/content':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={CameraMajor} color="base" />
            <span>Generations</span>
          </InlineStack>
        );
      case '/generation/queue':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ClockMajor} color="base" />
            <span>Generation Queue</span>
          </InlineStack>
        );
      case '/ai-design/imagine':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={PaintBrushMajor} color="base" />
            <span>SNAPS AI Imagine</span>
          </InlineStack>
        );
      case '/ai-design/edit':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={MagicMinor} color="base" />
            <span>SNAPS AI Edit</span>
          </InlineStack>
        );
      case '/ai-design/design-pro':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={MagicMinor} color="base" />
            <span>SNAPS AI PRO</span>
          </InlineStack>
        );
      case '/ai-design/expand':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={MaximizeMinor} color="base" />
            <span>SNAPS AI Expand</span>
          </InlineStack>
        );
      case '/ai-design/upscale':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ExploreImagesMajor} color="base" />
            <span>SNAPS AI Upscale</span>
          </InlineStack>
        );
      case '/ai-design/test':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ToolsMajor} color="base" />
            <span>SNAPS AI Test</span>
          </InlineStack>
        );
      case '/ai-design/test-multiple':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ToolsMajor} color="base" />
            <span>SNAPS AI Test Multiple</span>
          </InlineStack>
        );
      case '/ai-design/my-generations':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ImageMajor} color="base" />
            <span>My Generations</span>
          </InlineStack>
        );
      case '/ai-design/my-favourites':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={StarFilledMinor} color="base" />
            <span>My Favourites</span>
          </InlineStack>
        );
      case '/ai-design/all-generations':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ImageMajor} color="base" />
            <span>All Generations</span>
          </InlineStack>
        );
      case '/ai-design/story':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ContentMinor} color="base" />
            <span>SNAPS AI Story</span>
          </InlineStack>
        );
      case '/data-entry':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ImageMajor} color="base" />
            <span>Data Entry - Image Upload</span>
          </InlineStack>
        );
      case '/trials':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={OrdersMajor} color="base" />
            <span>Trials</span>
          </InlineStack>
        );
      case '/settings':
        return 'Settings';
      case '/settings/ai-models':
        return 'AI Models';
      case '/profile':
        return 'Profile';
      case '/orders/new':
        return 'Create Order';
      case '/orders':
        return (
          <InlineStack align="center" gap="300" blockAlign="center">
            <Icon source={ImageMajor} color="base" />
            <span>Orders</span>
            <div id="orders-date-filter-slot" />
          </InlineStack>
        );
      case '/accounting/summary':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={FinancesMinor} color="base" />
            <span>Accounting Summary</span>
          </InlineStack>
        );
      case '/accounting/payments':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={FinancesMinor} color="base" />
            <span>InstaPay Payments</span>
          </InlineStack>
        );
      case '/accounting/payouts':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={FinancesMinor} color="base" />
            <span>Payouts</span>
          </InlineStack>
        );
      case '/tickets':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={FlagMajor} color="base" />
            <span>Tickets</span>
          </InlineStack>
        );
      case '/users':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={CustomerPlusMajor} color="base" />
            <span>Users</span>
          </InlineStack>
        );
      case '/printings':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ExportMinor} color="base" />
            <span>Printings</span>
          </InlineStack>
        );
      case '/printings/booklet':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ExportMinor} color="base" />
            <span>Printings</span>
          </InlineStack>
        );
      case '/printings/verify':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ExportMinor} color="base" />
            <span>Printings Verify</span>
          </InlineStack>
        );
      case '/printings/express':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ExportMinor} color="base" />
            <span>Express Printings</span>
          </InlineStack>
        );
      case '/printings/express/booklet':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={ExportMinor} color="base" />
            <span>Express Printings</span>
          </InlineStack>
        );
      case '/offline/previews':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={GlobeMajor} color="base" />
            <span>Offline Previews</span>
          </InlineStack>
        );
      case '/offline/orders':
        return (
          <InlineStack align="center" gap="300" blockAlign="center">
            <Icon source={GlobeMajor} color="base" />
            <span>Offline Orders</span>
            <div id="orders-date-filter-slot" />
          </InlineStack>
        );
      case '/offline/generations':
        return (
          <InlineStack align="center" gap="100">
            <Icon source={GlobeMajor} color="base" />
            <span>Offline Generations</span>
          </InlineStack>
        );
      default:
        // Check if it's a ticket detail page
        if (path.startsWith('/tickets/')) {
          const ticketId = path.split('/')[2];
          return (
            <InlineStack align="center" gap="100">
              <Icon source={FlagMajor} color="base" />
              <span>Ticket {ticketId}</span>
            </InlineStack>
          );
        }
        // Check if it's an order detail page
        if (isOrderDetailPath(path)) {
          const orderId = path.split('/')[2];
          return (
            <InlineStack align="center" gap="100">
              <Icon source={ImageMajor} color="base" />
              <span>Order {orderId}</span>
            </InlineStack>
          );
        }
        return 'Dashboard';
    }
  };

  // Helper function to get subtitle based on current route
  const getSubtitle = () => {
    const path = location.pathname;
    switch (path) {
      case '/orders':
        // Get orders count from localStorage or state if available
        const ordersData = JSON.parse(localStorage.getItem('ordersData') || '{}');
        const totalOrders = ordersData.totalOrders || 0;
        return `${totalOrders} orders total`;
      case '/tickets':
        // Get tickets count from localStorage or state if available
        const ticketsData = JSON.parse(localStorage.getItem('ticketsData') || '{}');
        const totalTickets = ticketsData.totalTickets || 0;
        return `${totalTickets} tickets total`;
      case '/users':
        // Get users count from localStorage or state if available
        const usersData = JSON.parse(localStorage.getItem('usersData') || '{}');
        const totalUsers = usersData.totalUsers || 0;
        return `${totalUsers} users total`;
      case '/printings':
        return 'Generate booklet and hard-cover printing batches';
      case '/printings/booklet':
        return 'Generate booklet and hard-cover printing batches';
      case '/printings/express':
        return 'Generate and download each express generation separately';
      case '/printings/express/booklet':
        return 'Generate and download each express generation separately';
      default:
        // Check if it's a ticket detail page
        if (path.startsWith('/tickets/')) {
          const ticketDetailData = JSON.parse(localStorage.getItem('ticketDetailData') || '{}');
          if (ticketDetailData.status && ticketDetailData.createdAt) {
            return `${ticketDetailData.status} • ${ticketDetailData.createdAt}`;
          }
        }
        // Check if it's an order detail page
        if (isOrderDetailPath(path)) {
          const orderDetailData = JSON.parse(localStorage.getItem('orderDetailData') || '{}');
          if (orderDetailData.status && orderDetailData.createdAt) {
            return `${orderDetailData.status} • ${orderDetailData.createdAt}`;
          }
        }
        return undefined;
    }
  };

  // Helper function to get secondary actions based on current route
  const getSecondaryActions = () => {
    const path = location.pathname;
    if (/^\/printings\/[^/]+\/verify$/.test(path)) {
      return [{
        content: 'Open Scanner',
        icon: CameraMajor,
        onAction: () => {
          const event = new CustomEvent('printingsVerifyOpenScanner');
          window.dispatchEvent(event);
        }
      }];
    }
    switch (path) {
      case '/orders':
        return [
          ...(isSuperAdmin || hasManageOrders ? [{
            content: 'Import from Shopify',
            icon: ExportMinor,
            onAction: () => {
              // Dispatch event to OrdersPage to handle Shopify import
              const event = new CustomEvent('ordersImportShopify');
              window.dispatchEvent(event);
            }
          }] : []),
          {
            content: 'Sync Bosta Status',
            icon: RefreshMinor,
            onAction: () => {
              // Dispatch event to OrdersPage to handle Bosta status sync
              const event = new CustomEvent('ordersSyncBostaStatus');
              window.dispatchEvent(event);
            }
          },
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              // Dispatch event to OrdersPage to handle refresh
              const event = new CustomEvent('ordersRefresh');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/tickets':
        return [
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              // Dispatch event to TicketsPage to handle refresh
              const event = new CustomEvent('ticketsRefresh');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/users':
        return [
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              // Dispatch event to UsersPage to handle refresh
              const event = new CustomEvent('usersRefresh');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/printings':
        return [
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              // Dispatch event to PrintingsPage to handle refresh
              const event = new CustomEvent('printingsRefresh');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/printings/booklet':
        return [
          {
            content: 'Verify / Scan',
            icon: RefreshMinor,
            onAction: () => {
              const event = new CustomEvent('printingsVerifyScan');
              window.dispatchEvent(event);
            }
          },
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              const event = new CustomEvent('printingsRefresh');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/printings/verify':
        return [
          {
            content: 'Open Scanner',
            icon: CameraMajor,
            onAction: () => {
              const event = new CustomEvent('printingsVerifyOpenScanner');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/printings/express':
      case '/printings/express/booklet':
        return [
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              const event = new CustomEvent('expressPrintingsRefresh');
              window.dispatchEvent(event);
            }
          }
        ];
      case '/generation/stories':
        return [
          {
            content: 'Import Templates',
            icon: ExportMinor,
            onAction: () => {
              const event = new CustomEvent('openGenerationImportModal');
              window.dispatchEvent(event);
            }
          },
          {
            content: 'Font Library',
            icon: ToolsMajor,
            onAction: () => {
              const event = new CustomEvent('openGenerationFontLibraryModal');
              window.dispatchEvent(event);
            }
          },
          {
            content: 'Refresh',
            icon: RefreshMinor,
            onAction: () => {
              const event = new CustomEvent('refreshGenerationStories');
              window.dispatchEvent(event);
            }
          }
        ];
      default:
        // Check if it's a ticket detail page
        if (path.startsWith('/tickets/')) {
          return [
            {
              content: 'Back',
              icon: ArrowLeftMinor,
              onAction: () => {
                const event = new CustomEvent('ticketDetailBack');
                window.dispatchEvent(event);
              }
            },
            {
              content: 'Refresh',
              icon: RefreshMinor,
              onAction: () => {
                const event = new CustomEvent('ticketDetailRefresh');
                window.dispatchEvent(event);
              }
            }
          ];
        }
        // Check if it's an order detail page
        if (isOrderDetailPath(path)) {
          return [
            {
              content: 'Add Comment',
              icon: ChatMajor,
              onAction: () => {
                const event = new CustomEvent('orderDetailAddComment');
                window.dispatchEvent(event);
              }
            },
            {
              content: 'Add Flag',
              icon: FlagMajor,
              onAction: () => {
                const event = new CustomEvent('orderDetailAddFlag');
                window.dispatchEvent(event);
              }
            },
            {
              content: 'Refresh',
              icon: RefreshMinor,
              onAction: () => {
                const event = new CustomEvent('orderDetailRefresh');
                window.dispatchEvent(event);
              }
            },
            {
              content: 'Merge Order',
              icon: IconsMajor,
              destructive: true,
              onAction: () => {
                const event = new CustomEvent('orderDetailMergeOrder');
                window.dispatchEvent(event);
              }
            },
            {
              content: 'Download AWB',
              icon: PrintMinor,
              onAction: () => {
                const event = new CustomEvent('orderDetailDownloadAWB');
                window.dispatchEvent(event);
              }
            },
            {
              content: 'Recreate Shipping Order',
              icon: RefreshMajor,
              destructive: true,
              onAction: () => {
                const event = new CustomEvent('orderDetailRecreateShippingOrder');
                window.dispatchEvent(event);
              }
            }
          ];
        }
        return undefined;
    }
  };

  // Helper function to get primary action based on current route
  const getPrimaryAction = () => {
    const path = location.pathname;
    switch (path) {
      case '/previewer/stories':
        return {
          content: 'Add Story',
          icon: AddMajor,
          onAction: () => setIsCreateModalOpen(true),
          primary: true
        };
      case '/generation/montage-profiles':
        if (!hasManageStories && !isSuperAdmin) return undefined;
        return {
          content: 'Create Montage Profile',
          icon: AddMajor,
          onAction: () => window.dispatchEvent(
            new CustomEvent('openCreateMontageProfileModal')
          ),
          primary: true
        };
      case '/previewer/scenes':
        return {
          content: 'Add Scene',
          icon: AddMajor,
          onAction: () => setIsCreateSceneModalOpen(true),
          primary: true
        };
      case '/generation/stories':
        return {
          content: 'Add Generation Story',
          icon: AddMajor,
          onAction: () => {
            const event = new CustomEvent('openCreateGenerationStoryModal');
            window.dispatchEvent(event);
          },
          primary: true
        };
      case '/generation/scenes':
        return {
          content: 'Add Generation Scene',
          icon: AddMajor,
          onAction: () => {
            const event = new CustomEvent('openCreateGenerationSceneModal');
            window.dispatchEvent(event);
          },
          primary: true
        };
      case '/previewer/content':
        return {
          content: 'Generate Story Preview',
          icon: AddMajor,
          onAction: () => {
            const event = new CustomEvent('openCreatePreviewerModal');
            window.dispatchEvent(event);
          },
          primary: true
        };
      case '/generation/content':
        return {
          content: 'Generate Story',
          icon: AddMajor,
          onAction: () => {
            const event = new CustomEvent('openCreateGenerationModal');
            window.dispatchEvent(event);
          },
          primary: true
        };
      case '/orders':
        if (!(isSuperAdmin || hasManageOrders)) return undefined;
        return {
          content: 'Create Order',
          icon: AddMajor,
          onAction: () => {
            navigate('/orders/new');
          },
          primary: true
        };
      case '/accounting/payouts':
        return {
          content: 'Record Payout',
          icon: AddMajor,
          onAction: () => window.dispatchEvent(
            new CustomEvent('accountingPayoutsCreate')
          ),
          primary: true
        };
      case '/accounting/payments':
        if (activeCountry !== 'EG') return null;
        return {
          content: 'Record InstaPay Payment',
          icon: AddMajor,
          onAction: () => window.dispatchEvent(
            new CustomEvent('accountingInstapayCreate')
          ),
          primary: true
        };
      case '/tickets':
        return {
          content: 'Create Ticket',
          icon: AddMajor,
          onAction: () => {
            const event = new CustomEvent('ticketsCreate');
            window.dispatchEvent(event);
          },
          primary: true
        };
      case '/users':
        return {
          content: 'Add User',
          icon: CustomerPlusMajor,
          onAction: () => {
            const event = new CustomEvent('usersAddUser');
            window.dispatchEvent(event);
          },
          primary: true
        };
      case '/trials':
        return {
          content: 'Create Trial',
          icon: AddMajor,
          onAction: () => {
            const event = new CustomEvent('openCreateTrialModal');
            window.dispatchEvent(event);
          },
          primary: true
        };
      default:
        // Check if it's an order detail page
        if (isOrderDetailPath(path)) {
          return {
            content: 'Update Status',
            icon: EditMinor,
            onAction: () => {
              const event = new CustomEvent('orderDetailUpdateStatus');
              window.dispatchEvent(event);
            },
            primary: true
          };
        }
        return undefined;
    }
  };

  return (
    <Frame
      topBar={topBarMarkup}
      navigation={navigationMarkup}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={() => setMobileNavigationActive(false)}
    >
      <Page
        title={getPageTitle()}
        subtitle={getSubtitle()}
        primaryAction={getPrimaryAction()}
        secondaryActions={getSecondaryActions()}
      >
        <AppRoutes
          setToastMessage={setToastMessage}
          setToastActive={setToastActive}
          isCreateModalOpen={isCreateModalOpen}
          setIsCreateModalOpen={setIsCreateModalOpen}
          isCreateSceneModalOpen={isCreateSceneModalOpen}
          setIsCreateSceneModalOpen={setIsCreateSceneModalOpen}
        />
      </Page>
      {toastActive && (
        <Toast
          content={toastMessage}
          tone={toastMessage.includes('Error') ? 'critical' : 'success'}
          onDismiss={() => setToastActive(false)}
          action={{
            content: 'Dismiss',
            onAction: () => setToastActive(false),
          }}
        />
      )}
    </Frame>
  );
}

// Main App component that provides Router and Auth context
function App() {
  return (
    <AppProvider
      i18n={{
        Polaris: {
          ResourceList: {
            sortingLabel: 'Sort by',
            defaultItemSingular: 'item',
            defaultItemPlural: 'items',
            showing: 'Showing {itemsCount} {resource}',
            Item: {
              viewItem: 'View details for {itemName}',
            },
          },
          Common: {
            checkbox: 'checkbox',
          },
        },
      }}
    >
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </AppProvider>
  );
}

export default App;
